/**
 * Native mobile utilities using Capacitor
 * These functions work on both web and native platforms
 */

import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Share } from '@capacitor/share';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';

/**
 * Check if running on a native platform (iOS/Android)
 */
export const isNative = () => Capacitor.isNativePlatform();

/**
 * Check the current platform
 */
export const getPlatform = () => Capacitor.getPlatform(); // 'ios', 'android', or 'web'

/**
 * Take a photo using the device camera
 * Falls back to file input on web
 */
export const takePhoto = async (options = {}) => {
  try {
    const defaultOptions = {
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Prompt, // Let user choose camera or gallery
      width: 1024,
      height: 1024,
      preserveAspectRatio: true,
      ...options
    };

    const image = await Camera.getPhoto(defaultOptions);
    return {
      success: true,
      dataUrl: image.dataUrl,
      format: image.format,
      webPath: image.webPath
    };
  } catch (error) {
    console.error('Camera error:', error);
    return {
      success: false,
      error: error.message || 'Failed to capture photo'
    };
  }
};

/**
 * Pick a photo from the gallery only
 * Uses Android Photo Picker (no permissions required on Android 13+)
 */
export const pickFromGallery = async (options = {}) => {
  try {
    const defaultOptions = {
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Photos, // Gallery only - uses Photo Picker
      width: 1024,
      height: 1024,
      preserveAspectRatio: true,
      // Don't prompt for permissions - Photo Picker handles it
      promptLabelHeader: 'Select Photo',
      promptLabelPhoto: 'From Gallery',
      ...options
    };

    const image = await Camera.getPhoto(defaultOptions);
    return {
      success: true,
      dataUrl: image.dataUrl,
      format: image.format,
      webPath: image.webPath
    };
  } catch (error) {
    console.error('Gallery picker error:', error);
    // User cancelled - not an error
    if (error.message?.includes('cancel') || error.message?.includes('User')) {
      return { success: false, cancelled: true };
    }
    return {
      success: false,
      error: error.message || 'Failed to select photo'
    };
  }
};

/**
 * Take a photo with camera only
 */
export const takeWithCamera = async (options = {}) => {
  return takePhoto({
    ...options,
    source: CameraSource.Camera
  });
};

/**
 * Share content (story, character, etc.)
 */
export const shareContent = async ({ title, text, url, dialogTitle }) => {
  try {
    // Check if sharing is available
    const canShare = await Share.canShare();
    
    if (!canShare.value && !isNative()) {
      // Fallback for web browsers that don't support Web Share API
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url || text);
        return { success: true, method: 'clipboard' };
      }
      return { success: false, error: 'Sharing not supported' };
    }

    await Share.share({
      title: title || 'StoryWeaver',
      text: text || '',
      url: url || '',
      dialogTitle: dialogTitle || 'Share'
    });

    return { success: true, method: 'share' };
  } catch (error) {
    // User cancelled sharing - not an error
    if (error.message?.includes('cancel')) {
      return { success: false, cancelled: true };
    }
    console.error('Share error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Haptic feedback for button taps
 */
export const hapticTap = async () => {
  if (isNative()) {
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (e) {
      // Silently fail on devices without haptics
    }
  }
};

/**
 * Haptic feedback for success actions
 */
export const hapticSuccess = async () => {
  if (isNative()) {
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (e) {
      // Silently fail
    }
  }
};

/**
 * Haptic feedback for errors
 */
export const hapticError = async () => {
  if (isNative()) {
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (e) {
      // Silently fail
    }
  }
};

/**
 * Set status bar style (for native apps)
 */
export const setStatusBarStyle = async (style = 'light') => {
  if (isNative()) {
    try {
      await StatusBar.setStyle({ 
        style: style === 'light' ? Style.Light : Style.Dark 
      });
    } catch (e) {
      // Silently fail
    }
  }
};

/**
 * Set status bar color (Android only)
 */
export const setStatusBarColor = async (color = '#7c3aed') => {
  if (isNative() && getPlatform() === 'android') {
    try {
      await StatusBar.setBackgroundColor({ color });
    } catch (e) {
      // Silently fail
    }
  }
};

/**
 * Hide keyboard
 */
export const hideKeyboard = async () => {
  if (isNative()) {
    try {
      await Keyboard.hide();
    } catch (e) {
      // Silently fail
    }
  }
};

/**
 * Initialize native features when app starts
 */
export const initializeNative = async () => {
  if (isNative()) {
    try {
      // Set initial status bar style
      await setStatusBarStyle('light');
      await setStatusBarColor('#7c3aed');
      
      console.log(`StoryWeaver running on ${getPlatform()}`);
    } catch (e) {
      console.error('Failed to initialize native features:', e);
    }
  }
};

export default {
  isNative,
  getPlatform,
  takePhoto,
  pickFromGallery,
  takeWithCamera,
  shareContent,
  hapticTap,
  hapticSuccess,
  hapticError,
  setStatusBarStyle,
  setStatusBarColor,
  hideKeyboard,
  initializeNative
};
